import { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import { motion } from "framer-motion";
import { http } from "@/lib/api";
import { ALPHA2_TO_NUM } from "@/lib/countryCodes";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

/**
 * StoriesTravelMap — plots SpLink scan aggregates on a world map.
 *
 * `variant` controls the wrapping:
 *   - "hero"   → tall public-page hero, includes headline + legend
 *   - "widget" → compact card for the CMS dashboard, no headline
 */
export default function StoriesTravelMap({ variant = "hero", days = 30 }) {
  const [agg, setAgg] = useState({ countries: [], total: 0, since: null });
  const [hover, setHover] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      http.get(`/short/scans/aggregate?days=${days}`)
        .then(({ data }) => { if (!cancelled) setAgg(data); })
        .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [days]);

  const byNumericId = useMemo(() => {
    const out = {};
    for (const c of (agg.countries || [])) {
      const num = ALPHA2_TO_NUM[c.country];
      if (num) out[num] = c;
    }
    return out;
  }, [agg]);

  const maxCount = useMemo(
    () => Math.max(1, ...(agg.countries || []).map((c) => c.count)),
    [agg],
  );

  const isHero = variant === "hero";

  return (
    <section
      data-testid="stories-travel-map"
      className={isHero ? "py-14 md:py-20 px-6 md:px-10 max-w-[1600px] mx-auto" : ""}
    >
      {isHero && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-8">
          <div className="md:col-span-6">
            <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-4">
              / Where our stories travel
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              A pulse for every scan.
            </h2>
          </div>
          <p className="md:col-span-6 self-end text-[color:var(--np-text-2,#3A3830)] text-lg">
            Every QR scan and short-link click pinned to its country. Dot size
            scales with volume. Live from SpLink.pro — refreshed every minute.
          </p>
        </div>
      )}

      <div className="relative bg-[#FBF2E1] border border-black/10 overflow-hidden rounded-2xl">
        <ComposableMap
          projection="geoEqualEarth"
          projectionConfig={{ scale: isHero ? 165 : 140 }}
          style={{ width: "100%", height: "auto" }}
          data-testid="stories-travel-map-svg"
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const hit = byNumericId[geo.id];
                const fill = hit ? "#F5B342" : "#FDE8CC";
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke="#B85F2A"
                    strokeWidth={0.3}
                    style={{
                      default: { outline: "none" },
                      hover: { fill: hit ? "#EA580C" : "#F5D19A", outline: "none" },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>

          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies
                .filter((geo) => byNumericId[geo.id])
                .map((geo) => {
                  const c = byNumericId[geo.id];
                  const [lon, lat] = geoCentroid(geo);
                  const scale = Math.max(0.35, Math.min(1, c.count / maxCount));
                  const r = 3 + scale * 12;
                  return (
                    <Marker key={geo.rsmKey} coordinates={[lon, lat]}>
                      <g
                        onMouseEnter={() => setHover({ ...c, name: geo.properties?.name })}
                        onMouseLeave={() => setHover(null)}
                        style={{ cursor: "pointer" }}
                      >
                        <circle r={r} fill="#B85F2A" fillOpacity={0.25} />
                        <circle r={Math.max(2.5, r * 0.5)} fill="#B85F2A" stroke="#241209" strokeWidth={0.6} />
                      </g>
                    </Marker>
                  );
                })
            }
          </Geographies>
        </ComposableMap>

        {hover && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-4 right-4 max-w-xs bg-white/95 backdrop-blur border border-black/10 rounded-2xl p-4 shadow-sm"
            data-testid="stories-travel-map-hover"
          >
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-1">
              {hover.country}
            </div>
            <div className="font-serif-display text-2xl leading-tight tracking-tight">
              {hover.name || hover.country}
            </div>
            <div className="mt-2 text-sm text-[#3A3830]">
              <strong>{hover.count.toLocaleString()}</strong> scan{hover.count === 1 ? "" : "s"}
              {hover.last_at && (
                <span className="text-[#8E8B7F]"> · last {new Date(hover.last_at).toLocaleString()}</span>
              )}
            </div>
            {hover.cities && hover.cities.length > 0 && (
              <div className="mt-2 text-[11px] text-[#3A3830]">
                <span className="font-mono-tech text-[9px] tracking-[0.25em] uppercase text-[#8E8B7F]">Cities: </span>
                {hover.cities.join(", ")}
              </div>
            )}
          </motion.div>
        )}

        <div className="absolute bottom-3 left-4 bg-white/90 backdrop-blur border border-black/10 rounded-full px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.3em] uppercase text-[#3A3830]">
          {agg.total.toLocaleString()} scans · {agg.countries?.length || 0} countries · last {days}d
        </div>
      </div>
    </section>
  );
}
